import React, { useState, useContext } from 'react'
import { GlobalContext } from '../GlobalContext'
import Button from '../formfields/Button'
import Text from '../formfields-new/Text'
import RedAlert from '../ui-elements/RedAlert'
import Password from '../formfields-new/Password'

const SHARED_DEVICE_KEY = 'isSharedDevice'
const SHARED_DEVICE_EXPIRY_KEY = 'sharedDeviceExpiry'
const SHARED_DEVICE_EXPIRY_MS = 28 * 24 * 60 * 60 * 1000

type Props = {
  pin?: string
  allowRememberMe?: number
  setShowForm: React.Dispatch<React.SetStateAction<any>>
}


function GuestUserLoginForm({
  pin,
  allowRememberMe,
  setShowForm
}: Props) {

  const {
    setShowModal,
    sendMessageToWebsocket,
    currentOrganisation,
    orgIdFromQrCode,
    t,
    getUserStats
  } = useContext(GlobalContext)

  const showSharedDeviceFlow = !!(allowRememberMe && allowRememberMe > 0)

  const initialFormData = { "UserName": { "value": '', "required": true, "type": "text" } }
  const [formFields, setFormFields] = useState<ObjectStringKeyAnyValue>(initialFormData)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [isSharedDevice, setIsSharedDevice] = useState<boolean | null>(() => {
    if (!showSharedDeviceFlow) return null
    const stored = localStorage.getItem(SHARED_DEVICE_KEY)
    if (stored === 'true') {
      const expiry = parseInt(localStorage.getItem(SHARED_DEVICE_EXPIRY_KEY) || '0', 10)
      if (expiry > Date.now()) return true
      localStorage.removeItem(SHARED_DEVICE_KEY)
      localStorage.removeItem(SHARED_DEVICE_EXPIRY_KEY)
    }
    return null
  })

  const [sharedDeviceHint] = useState<boolean | null>(() => {
    if (!showSharedDeviceFlow) return null
    return localStorage.getItem(SHARED_DEVICE_KEY) === 'false' ? false : null
  })

  const [rememberMe, setRememberMe] = useState(false)

  const selectedHint = isSharedDevice ?? sharedDeviceHint

  const handleSharedDeviceChange = (value: boolean) => {
    setIsSharedDevice(value)
    localStorage.setItem(SHARED_DEVICE_KEY, String(value))
    if (value) {
      localStorage.setItem(SHARED_DEVICE_EXPIRY_KEY, String(Date.now() + SHARED_DEVICE_EXPIRY_MS))
      setRememberMe(false)
    } else {
      localStorage.removeItem(SHARED_DEVICE_EXPIRY_KEY)
    }
  }

  const showQuestion = showSharedDeviceFlow && isSharedDevice === null
  const showForm = !showSharedDeviceFlow || isSharedDevice !== null


  const validate = () => {
    let isValid = true
    let errorMessage = ''
    if (
      !formFields["UserName"]['value'] ||
      formFields["UserName"]['value'].trim() === ''
    ) {
      isValid = false
      errorMessage = `${errorMessage} ${t('Auth.GuestUserLoginForm.pleaseEnterFullName')} `
    }

    if (pin) {
      if (!formFields['pin'] || !formFields['pin'].value) {
        isValid = false
        errorMessage = `${errorMessage} ${t('Auth.GuestUserLoginForm.pleaseEnterPin')} `
      }
    }
    setErrorMessage(errorMessage)
    return isValid
  }


  const validateAndSave = () => {
    const isValid = validate()
    if (isValid) {
      setErrorMessage('')
      setShowModal({ "spinner": "Logging you in..." })
      const payload: ObjectStringKeyAnyValue = {
        action: 'guestLogin',
        guestLoginAction: 'guestLoginRequestFromLoginForm',
        "UserName": `${formFields['UserName'].value}`,
        "OrganisationId": `${orgIdFromQrCode ? orgIdFromQrCode : currentOrganisation}`,
        userStats: getUserStats()
      }
      if (formFields && formFields['pin'] && formFields['pin'].value) {
        payload['pin'] = formFields['pin'].value
      }
      if (rememberMe && isSharedDevice === false) {
        payload['rememberMe'] = true
      }
      sendMessageToWebsocket(JSON.stringify(payload))
    }
  }



  return <div className={`w-full flex flex-col gap-4`}>

    <h3 className={`font-righteous text-2xl`}>{t('Auth.GuestUserLoginForm.guestLogIn')}</h3>

    {showQuestion && <div className={`flex flex-col gap-2 items-center text-center`}>
      <p>{t('Auth.GuestUserLoginForm.isSharedDevice')}</p>
      <div className={`flex flex-row gap-2 justify-center`}>
        <button
          type='button'
          onClick={() => handleSharedDeviceChange(true)}
          className={`px-4 py-2 rounded font-bold text-sm border-2 ${selectedHint === true ? 'border-brandmidblue bg-brandmidblue text-white' : 'border-gray-300 bg-gray-100 text-gray-700'}`}
        >
          {t('Auth.GuestUserLoginForm.sharedDeviceYes')}
        </button>
        <button
          type='button'
          onClick={() => handleSharedDeviceChange(false)}
          className={`px-4 py-2 rounded font-bold text-sm border-2 ${selectedHint === false ? 'border-brandmidblue bg-brandmidblue text-white' : 'border-gray-300 bg-gray-100 text-gray-700'}`}
        >
          {t('Auth.GuestUserLoginForm.sharedDeviceNo')}
        </button>
      </div>
    </div>}

    {showForm && <>
      <div className={`flex flex-col gap-2`}>
        <p>{t('Auth.GuestUserLoginForm.fullName')}</p>
        <Text
          labelText={''}
          formFields={formFields}
          setFormFields={setFormFields}
          fieldName={'UserName'}
          placeholder={''}
          errorMessage={t('Auth.GuestUserLoginForm.pleaseEnterFullName')}
          size={'big'}
          center={true}
          value={formFields && formFields['UserName'] && formFields['UserName'].value || ''}
        />
      </div>

      {pin && <div className={`flex flex-col gap-2`}>
        <p>{t('Auth.GuestUserLoginForm.organisationPin')}:</p>
        <Password
          labelText={''}
          formFields={formFields}
          setFormFields={setFormFields}
          fieldName={'pin'}
          placeholder={''}
          errorMessage={t('Auth.GuestUserLoginForm.pleaseEnterPin')}
          size={'big'}
          center={true}
          value={formFields && formFields['pin'] && formFields['pin'].value || ''}
        />
      </div>}

      {errorMessage && <RedAlert>{errorMessage}</RedAlert>}

      <div className={`w-full flex flex-row gap-4`}>
        <Button
          onClick={() => setShowForm(null)}
          text={t('Buttons.cancel')}
          variant={'gray'}
          size={'big'}
          sameWindow={true}
        />
        <Button
          onClick={validateAndSave}
          text={t('Buttons.next')}
          variant={'secondary'}
          size={'big'}
          sameWindow={true}
        />
      </div>

      {isSharedDevice === false && allowRememberMe && allowRememberMe > 0 && <label className={`flex flex-row gap-2 items-center cursor-pointer`}>
        <input
          type='checkbox'
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          className={`w-5 h-5`}
        />
        <span>{t('Auth.GuestUserLoginForm.rememberMe')}</span>
      </label>}
    </>}

    {showQuestion && <Button
      onClick={() => setShowForm(null)}
      text={t('Buttons.cancel')}
      variant={'gray'}
      size={'big'}
      sameWindow={true}
    />}
  </div>
}
export default GuestUserLoginForm
